/**
 * ParentFamilyVault.js — Parent-side mirror of every teen's vault.
 *
 * Renders one of three states per teen, matching the teen-side vault
 * lifecycle (see /memes / MemeGallery.js):
 *
 *   1) LOCKED       — teen hasn't entered their 6-digit combination yet.
 *                     We show a quiet locked-vault placeholder with the
 *                     tenure countdown. No NFTs visible (mirror is sealed).
 *
 *   2) MIRROR       — teen has opened their vault. Every NFT mirrors here
 *                     read-only so the parent can see what their teen has
 *                     earned, but no Crossmint buttons appear (no consent).
 *
 *   3) TRADE_READY  — teen has marked one or more NFTs for trade on their
 *                     vault page. ONLY those marked items get the
 *                     "Run on Crossmint" button. "Keep" items stay
 *                     read-only.
 *
 * Powers the new "parent-executes-only" legal model (see backend
 * services/state_trading_rules.py).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Gem, ExternalLink, Lock, Hourglass, Sparkles, AlertCircle, Loader2, Heart } from 'lucide-react';
import { apiClient } from '../../api/client';

const ParentFamilyVault = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await apiClient.request('/api/parent/trade-vault');
      setData(r);
    } catch (e) {
      setError(e?.message || 'Could not load the Family Vault.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Sprint 208 Revised item #3 — Telepathic SSE vault mirror. Subscribe to
  // the milestone bus and reload our state in <200ms whenever the teen
  // taps Keep or Trade on any meme. The same EventSource the milestone
  // celebration uses; we just listen for the `vault_choice_made` event.
  useEffect(() => {
    const token = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
    if (!token || typeof EventSource === 'undefined') return;
    const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
    let es;
    try {
      es = new EventSource(`${apiUrl}/api/teen/milestones/stream?token=${encodeURIComponent(token)}`);
      es.addEventListener('vault_choice_made', () => {
        // Refresh from source of truth — keeps reasoning identical to the
        // initial load path and avoids state-shape drift.
        load();
      });
    } catch { /* SSE unavailable — manual refresh still works */ }
    return () => { if (es) { try { es.close(); } catch {} } };
  }, [load]);

  const runOnCrossmint = async collectibleId => {
    setExecuting(collectibleId);
    try {
      const r = await apiClient.request(`/api/parent/trade-vault/${collectibleId}/execute`, { method: 'POST' });
      if (r.redirect_url) {
        window.open(r.redirect_url, '_blank', 'noopener,noreferrer');
        setTimeout(load, 800);
      }
    } catch (e) {
      setError(e?.message || 'Could not start the trade.');
    } finally {
      setExecuting(null);
    }
  };

  if (loading) {
    return (
      <section data-testid="parent-family-vault" className="bg-white rounded-2xl border border-amber-200/60 p-6 shadow-sm">
        <h2 className="text-base font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
          <Gem size={16} style={{ color: '#F5831F' }} /> Family Vault
        </h2>
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin text-gray-400" />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section data-testid="parent-family-vault" className="bg-white rounded-2xl border border-red-200 p-6 shadow-sm">
        <p className="text-sm text-red-600 flex items-center gap-1.5"><AlertCircle size={14} /> {error}</p>
      </section>
    );
  }

  const summary = data?.summary || {};
  const teens = data?.teens || [];

  return (
    <section
      data-testid="parent-family-vault"
      className="bg-white rounded-2xl border border-amber-200/60 p-6 shadow-sm"
      style={{ background: 'linear-gradient(180deg, #FFFDF8 0%, #FFFFFF 60%)' }}
    >
      <div className="flex items-start justify-between mb-1">
        <div>
          <h2 className="text-base font-bold flex items-center gap-2" style={{ color: '#1A1209' }}>
            <Gem size={16} style={{ color: '#F5831F' }} /> Family Vault
          </h2>
          <p className="text-[12px] mt-0.5" style={{ color: '#6B5944' }}>
            Mirrors each teen&apos;s vault once they&apos;ve opened it. Teens choose which
            collectibles to trade — you run those trades on your own verified
            Crossmint account.
          </p>
        </div>
        <div className="flex gap-3 text-right text-[11px]" style={{ color: '#6B5944' }}>
          <div data-testid="vault-summary-open">
            <div className="text-base font-bold" style={{ color: '#10B981' }}>
              {summary.teens_with_open_vault || 0}/{summary.teen_count || 0}
            </div>
            <div>Vaults open</div>
          </div>
          <div data-testid="vault-summary-collectibles">
            <div className="text-base font-bold" style={{ color: '#1A1209' }}>{summary.total_collectibles || 0}</div>
            <div>Mirrored</div>
          </div>
          <div data-testid="vault-summary-marked">
            <div className="text-base font-bold" style={{ color: '#F5831F' }}>{summary.marked_for_trade || 0}</div>
            <div>To trade</div>
          </div>
        </div>
      </div>

      {teens.length === 0 && (
        <div className="mt-5 rounded-xl border border-dashed border-amber-200 p-5 text-center" data-testid="vault-empty">
          <Sparkles size={20} className="mx-auto mb-2" style={{ color: '#F5831F' }} />
          <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>Nothing in the vault yet</p>
          <p className="text-xs mt-1" style={{ color: '#6B5944' }}>
            Once a teen joins your family Charter, their vault status will appear here.
          </p>
        </div>
      )}

      {teens.map(t => (
        <TeenVaultBlock key={t.teen_id} teen={t} executing={executing} onRun={runOnCrossmint} />
      ))}

      <p className="mt-4 text-[10px] italic" style={{ color: '#9CA3AF' }}>
        Why you run the trade: under-18s cannot legally form binding commercial contracts
        in any US state. By executing on your own verified Crossmint account, the trade
        is binding, KYC-compliant, and the proceeds (and any tax) sit with the adult.
      </p>
    </section>
  );
};


const TeenVaultBlock = ({ teen, executing, onRun }) => {
  const stateLabel = teen.vault_state === 'locked'
    ? 'Vault locked'
    : teen.vault_state === 'mirror'
      ? 'Vault open · mirrored'
      : 'Trade requested';
  const stateColor = teen.vault_state === 'locked'
    ? '#9CA3AF'
    : teen.vault_state === 'mirror'
      ? '#10B981'
      : '#F5831F';

  return (
    <div className="mt-5 first:mt-4" data-testid={`vault-teen-${teen.teen_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: '#1A1209' }}>{teen.teen_name}</h3>
          <p className="text-[11px]" style={{ color: '#6B5944' }}>{teen.teen_email}</p>
        </div>
        <span
          className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full"
          style={{ backgroundColor: `${stateColor}15`, color: stateColor }}
          data-testid={`vault-state-${teen.teen_id}`}
        >
          {stateLabel}
        </span>
      </div>

      {/* ── LOCKED ─────────────────────────────────────────────────────── */}
      {teen.vault_state === 'locked' && (
        <div className="rounded-xl border border-dashed border-gray-300 p-5 text-center bg-gray-50" data-testid={`vault-locked-${teen.teen_id}`}>
          <Lock size={20} className="mx-auto mb-2" style={{ color: '#9CA3AF' }} />
          <p className="text-sm font-semibold" style={{ color: '#1A1209' }}>
            {teen.teen_name.split(' ')[0]}&apos;s vault is sealed
          </p>
          <p className="text-xs mt-1" style={{ color: '#6B5944' }}>
            {teen.tenure_eligible_now ? (
              <>
                Their 180 days are up — they can request the 6-digit
                combination on their vault page any time. Once they enter it,
                every collectible will mirror here.
              </>
            ) : (
              <>
                <Hourglass size={10} className="inline mr-1" style={{ color: '#F5831F' }} />
                {teen.tenure_days_left} days remaining. When the countdown
                hits zero, {teen.teen_name.split(' ')[0]} will receive a
                6-digit combination to open their vault — and everything
                inside will mirror here.
              </>
            )}
          </p>
        </div>
      )}

      {/* ── MIRROR ─────────────────────────────────────────────────────── */}
      {teen.vault_state === 'mirror' && (
        <>
          <p className="text-[11px] mb-3" style={{ color: '#6B5944' }}>
            Vault is open. {teen.teen_name.split(' ')[0]} hasn&apos;t asked you to trade
            anything yet — these are read-only until they tap &quot;Trade this one&quot; on
            their vault page.
          </p>
          <CollectibleGrid items={teen.collectibles} executing={executing} onRun={onRun} />
        </>
      )}

      {/* ── TRADE READY ───────────────────────────────────────────────── */}
      {teen.vault_state === 'trade_ready' && (
        <>
          <div
            className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3"
            data-testid={`vault-trade-banner-${teen.teen_id}`}
          >
            <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#92400E' }}>
              {teen.marked_for_trade_count} trade request{teen.marked_for_trade_count === 1 ? '' : 's'} from {teen.teen_name.split(' ')[0]}
            </p>
            <p className="text-xs mt-1" style={{ color: '#1A1209' }}>
              Tap &quot;Run on Crossmint&quot; next to the marked NFTs below to list or
              transfer them on your own verified account.
            </p>
          </div>
          <CollectibleGrid items={teen.collectibles} executing={executing} onRun={onRun} highlightMarked />
        </>
      )}
    </div>
  );
};


const CollectibleGrid = ({ items, executing, onRun, highlightMarked = false }) => {
  if (!items || items.length === 0) {
    return <p className="text-xs italic" style={{ color: '#9CA3AF' }}>No collectibles yet.</p>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map(c => {
        const isMarked = c.marked_for_trade;
        const borderColor = isMarked && highlightMarked ? '#F5831F60' : isMarked ? '#F5831F30' : '#E5E5E5';
        const bgColor = isMarked && highlightMarked ? '#FFF7EA' : isMarked ? '#FFFAF2' : '#FFFFFF';
        return (
          <div
            key={c.collectible_id}
            data-testid={`vault-collectible-${c.collectible_id}`}
            className="rounded-xl border p-3 flex gap-3 items-start"
            style={{ borderColor, backgroundColor: bgColor }}
          >
            <div
              className="w-12 h-12 rounded-lg flex-shrink-0 bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center overflow-hidden"
              style={{ backgroundImage: c.image_url ? `url(${c.image_url})` : undefined, backgroundSize: 'cover', backgroundPosition: 'center' }}
            >
              {!c.image_url && <Gem size={18} style={{ color: '#F5831F' }} />}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate" style={{ color: '#1A1209' }}>{c.name}</p>
              {isMarked ? (
                <p className="text-[11px] flex items-center gap-1 mt-0.5" style={{ color: '#F5831F' }}>
                  <Sparkles size={10} /> Teen marked this for trade
                </p>
              ) : (
                <p className="text-[11px] flex items-center gap-1 mt-0.5" style={{ color: '#6B5944' }}>
                  <Heart size={10} /> Teen is keeping this (read-only)
                </p>
              )}
              {isMarked && (
                <button
                  onClick={() => onRun(c.collectible_id)}
                  disabled={executing === c.collectible_id}
                  data-testid={`vault-run-crossmint-${c.collectible_id}`}
                  className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-semibold text-white disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #F5831F, #FFB073)' }}
                >
                  {executing === c.collectible_id ? <Loader2 size={10} className="animate-spin" /> : <ExternalLink size={10} />}
                  Run on Crossmint
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ParentFamilyVault;
